import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { StudentService } from '../../../../../services/student';
import {
  IStudent,
  IStudentEnrollments,
} from '../../../../../interfaces/student';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { Helpers } from '../../../../../utilities/helpers';
import { FlashService } from '../../../../../services/flash';
import { SubjectService } from '../../../../../services/subject';
import { ILevelSubjects } from '../../../../../interfaces/subject';
import { FeeService } from '../../../../../services/fees';
import {
  IFeeStructure,
  IFeeStructures,
  IPayments,
  IStudentFee,
  IStudentFees,
} from '../../../../../interfaces/fees';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { DecimalPipe, NgClass } from '@angular/common';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { ShimmerEffect } from '../../../../../shared/shimmer-effect/shimmer-effect';
import { AcademicYearService } from '../../../../../services/academic-year';
import { IAcademicYear, IAcademicYears } from '../../../../../interfaces/academic-year';
import { Session } from '../../../../../services/session';
import { UserService } from '../../../../../services/user';
import { IUser } from '../../../../../interfaces/user';
import { ClassService } from '../../../../../services/class';
import { IClasses } from '../../../../../interfaces/class';
import { AttendanceService } from '../../../../../services/attendance';
import { IAttendanceReports } from '../../../../../interfaces/attendance';
import { AcademicTermService } from '../../../../../services/academic-term';
import { IAcademicTerms } from '../../../../../interfaces/academic-term';
import { Calender } from '../../../../../shared/calender/calender';

@Component({
  selector: 'app-student-detail',
  imports: [
    ActionButton,
    LoadingIndicator,
    NotFoundError,
    ReactiveFormsModule,
    NgClass,
    DecimalPipe,
    Pagination,
    ShimmerEffect,
    FormsModule,
    Calender,
  ],
  templateUrl: './student-detail.html',
  styleUrl: './student-detail.css',
})
export class StudentDetail {
  constructor(
    private activatedRoute: ActivatedRoute,
    private studentService: StudentService,
    private subjectService: SubjectService,
    private feeService: FeeService,
    protected academicYearService: AcademicYearService,
    private session: Session,
    private userService: UserService,
    private classService: ClassService,
    private attendanceService: AttendanceService,
    protected academicTermService: AcademicTermService,
  ) {
    this.activatedRoute.queryParamMap.subscribe((params) => {
      this.studentId = params.get('id')!;
    });

    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;

    this.getStudentEnrollment(this.studentId);
    this.getStudentFees();
    this.getAcademicYears();
    this.getStudent(this.studentId);
  }

  protected studentId!: string;
  protected student = signal<IStudent | null>(null);
  protected studentEnrollment = signal<IStudentEnrollments | null>(null);
  protected isLoading = signal<boolean>(false);
  protected readonly Helpers = Helpers;
  protected readonly flashService = inject(FlashService);
  protected levelSubjects = signal<ILevelSubjects | null>(null);
  protected studentFeePayments = signal<IPayments | null>(null);
  protected studentFees = signal<IStudentFees | null>(null);
  protected isFiltering = signal<boolean>(false);
  protected setPaymentDetail = signal(false);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected pagination = signal({ page: 0, size: 5 });
  protected setDiscount = signal<boolean>(false);
  protected setWaive = signal<boolean>(false);
  protected setReverseWaiver = signal<boolean>(false);
  protected academicYears = signal<IAcademicYears | null>(null);
  protected academicYearId: string = '';
  protected search: string = '';
  protected admins = ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'FINANCE_ADMIN'];
  protected schoolAdmins = ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'FINANCE_ADMIN'];
  protected loggedUserRole?: string;
  protected adjustmentAmount!: number;
  protected discountAmount!: number;
  protected adjustmentReason!: string;
  protected isSubmitting = signal<boolean>(false);
  protected readonly Number = Number;
  protected waiveReason!: string;
  protected reversalReason: string = '';
  protected waivedBy = signal<IUser | null>(null);
  protected editStatuses = ['UNPAID', 'PARTIALLY_PAID', 'OVERDUE'];
  protected setRecordPayment = signal<boolean>(false);
  protected paymentAmount: string = '';
  protected paymentDate: string = '';
  protected paymentMethod: string = '';
  protected transactionReference!: string;
  protected additionalNotes: string = '';
  protected readonly paymentMethods = ['CASH', 'MOBILE_MONEY', 'BANK_TRANSFER', 'CARD', 'CHEQUE'];
  protected assignClassCheck = signal<boolean>(false);
  protected classes = signal<IClasses | null>(null);
  protected classId = '';
  protected className = '';
  protected classPublicId = '';
  protected academicYear = signal<IAcademicYear | null>(null);
  protected startDate = '';
  protected endDate = '';
  protected enrollmentDate = '';
  protected leaveDays: number[] = [];
  protected attendanceReports = signal<IAttendanceReports | null>(null);
  protected academicTerms = signal<IAcademicTerms | null>(null);
  protected attendanceAcademicYearId = '';
  protected attendanceTermId = '';
  protected attendanceTermStartDate = '';
  protected attendanceTermEndDate = '';
  protected attendanceSummary = signal({
    present: 0,
    absent: 0,
    total: 0,
    rate: 0,
    noRecord: 0,
  });
  protected selectedTermName = '';
  protected selectedAcademicYearName = '';
  protected invalidStatuses = [
    'INACTIVE',
    'COMPLETED',
    'ARCHIVED',
    'DEACTIVATED',
    'SUSPENDED',
    'GRADUATED',
    'TRANSFERRED',
    'WITHDRAWN',
  ];
  protected setAssignFee = signal<boolean>(false);
  protected feeQuery = '';
  protected feeStructuresToAssign = signal<IFeeStructures | null>(null);
  protected selectedFee: IFeeStructure['payload'] | undefined;
  protected selectedFeeStructure = signal<IFeeStructures | null>(null);

  protected selectedStudentFee: IStudentFee['payload'] = {
    id: '',
    public_id: '',
    school_id: '',
    academic_year_id: '',
    term_id: '',
    level_id: '',
    student_id: '',
    enrollment_id: '',
    fee_structure_id: '',
    fee_name: '',
    student_public_id: '',
    enrollment_public_id: '',
    original_amount: '',
    discount_amount: '',
    adjustment_amount: '',
    adjustment_reason: '',
    final_amount: '',
    due_date: '',
    waiver_reason: '',
    waived_by: '',
    class_public_id: '',
    class_name: '',
    fee_structure_public_id: '',
    status: '',
    academic_year_public_id: '',
    academic_year_name: '',
    term_public_id: '',
    term_name: '',
    level_public_id: '',
    level_name: '',
    level_code: '',
    superseded_by_enrollment_id: '',
    superseded_at: '',
    created_at: '',
    updated_at: '',
  };

  protected getStudent(id: string) {
    this.isLoading.set(true);

    this.studentService.getStudentById(`${ApiEndpoints.url}/student/${id}`).subscribe({
      next: (data: IStudent) => {
        this.student.set(data);
        this.isLoading.set(false);
      },
    });
  }

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Years. Refresh the page.',
          );
        },
      });
  }

  protected selectStudentFee(fee: IStudentFee['payload']) {
    if (fee.status === 'WAIVED') {
      this.getWaivedByUser(fee.waived_by);
    }

    this.selectedStudentFee = fee;
    this.getStudentPayments(fee.id);
    this.setPaymentDetail.set(true);
  }

  protected getStudentEnrollment(studentId: string) {
    const params = {
      page: 0,
      size: 1,
      student_id: studentId,
      status: 'ACTIVE',
    };

    this.studentService
      .getStudentEnrollments(`${ApiEndpoints.url}/student-enrollment`, params)
      .subscribe({
        next: (data) => {
          this.studentEnrollment.set(data);
          if (data.payload.length) {
            this.getLevelSubjects(data.payload[0].level_id);
            this.onAttendanceAcademicYearChange();
          }
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Error getting student enrollments.');
        },
      });
  }

  protected getLevelSubjects(levelId: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
      level_id: levelId,
    };

    this.subjectService.getLevelSubjects(`${ApiEndpoints.url}/level-subject`, params).subscribe({
      next: (res) => {
        this.levelSubjects.set(res);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load subjects. Refresh the page',
        );
      },
    });
  }

  protected onAttendanceAcademicYearChange(): void {
    this.attendanceTermId = '';
    this.academicTerms.set(null);
    this.attendanceReports.set(null);
    this.attendanceSummary.set({ present: 0, absent: 0, total: 0, rate: 0, noRecord: 0 });
    this.selectedTermName = '';
    this.selectedAcademicYearName = '';
    this.attendanceTermStartDate = '';
    this.attendanceTermEndDate = '';

    if (!this.attendanceAcademicYearId) {
      return;
    }

    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
      academic_year_id: this.attendanceAcademicYearId,
    };

    this.academicTermService
      .getAcademicTerms(`${ApiEndpoints.url}/academic-term`, params)
      .subscribe({
        next: (res) => {
          this.academicTerms.set(res);
          if (res.payload.length) {
            this.getAttendance();
          }
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Terms. Refresh the page.',
          );
        },
      });
  }

  protected getAttendance(): void {
    if (!this.studentId || !this.attendanceAcademicYearId || !this.attendanceTermId) {
      return;
    }

    const selectedTerm = this.academicTerms()?.payload.find((t) => t.id === this.attendanceTermId);
    this.attendanceTermStartDate = selectedTerm?.term_start_date ?? '';
    this.attendanceTermEndDate = selectedTerm?.term_end_date ?? '';

    const params: Record<string, string | number> = {
      page: 0,
      size: 100,
      student_id: this.studentId,
      academic_year_id: this.attendanceAcademicYearId,
      term_id: this.attendanceTermId,
    };

    this.attendanceService
      .getAttendanceReports(`${ApiEndpoints.url}/attendance-record`, params)
      .subscribe({
        next: (data) => {
          this.attendanceReports.set(data);
          this.selectedTermName =
            this.academicTerms()?.payload.find((t) => t.id === this.attendanceTermId)?.term_name ??
            '';
          this.selectedAcademicYearName =
            this.academicYears()?.payload.find((a) => a.id === this.attendanceAcademicYearId)
              ?.name ?? '';
          this.updateAttendanceSummary(data);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load attendance. Refresh the page.',
          );
        },
      });
  }

  protected updateAttendanceSummary(
    data: IAttendanceReports | null = this.attendanceReports(),
  ): void {
    const records = data?.payload ?? [];
    const present = records.filter((r) => r.attendance_status?.toUpperCase() === 'PRESENT').length;
    const absent = records.filter((r) => r.attendance_status?.toUpperCase() === 'ABSENT').length;
    const noRecord = this.countMissedAttendanceDays(records);
    const total = present + absent + noRecord;
    const rate = total > 0 ? Math.round((present / total) * 100) : 0;

    this.attendanceSummary.set({ present, absent, total, rate, noRecord });
  }

  private countMissedAttendanceDays(records: IAttendanceReports['payload']): number {
    const start = (this.attendanceTermStartDate ?? '').slice(0, 10);

    if (!start) {
      return 0;
    }

    const today = this.dateKey(new Date());
    const termEnd = (this.attendanceTermEndDate ?? '').slice(0, 10);
    const end = termEnd && termEnd < today ? termEnd : today;

    if (end < start) {
      return 0;
    }

    const recordedDates = new Set(
      records
        .map((record) => (record.attendance_date ?? '').slice(0, 10))
        .filter((date) => date.length > 0),
    );

    let missed = 0;
    const cursor = new Date(`${start}T00:00:00`);
    const last = new Date(`${end}T00:00:00`);

    while (cursor <= last) {
      const dayOfWeek = cursor.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      if (!isWeekend && !recordedDates.has(this.dateKey(cursor))) {
        missed++;
      }

      cursor.setDate(cursor.getDate() + 1);
    }

    return missed;
  }

  private dateKey(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  protected getStudentFees(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isFiltering.set(true);

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
      student_id: this.studentId,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.feeService.getStudentFees(`${ApiEndpoints.url}/student-fee`, params).subscribe({
      next: (res) => {
        this.studentFees.set(res);
        this.isFiltering.set(false);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load fees. Refresh the page');
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.studentFees();
  }

  protected getStudentPayments(studentFeeId: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 100,
      student_fee_id: studentFeeId,
      status: 'COMPLETED',
    };

    this.feeService.getPayments(`${ApiEndpoints.url}/payment`, params).subscribe({
      next: (res) => {
        this.studentFeePayments.set(res);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load payments. Refresh the page',
        );
      },
    });
  }

  protected get paymentRemainingBalance(): number | null {
    const payments = this.studentFeePayments()?.payload;

    if (payments?.length) {
      return payments[0].student_fee_remaining_balance;
    }

    return this.selectedStudentFee?.id ? Number(this.selectedStudentFee.final_amount) : null;
  }

  protected get selectedStudentFeeFinalAmount(): number | null {
    return this.selectedStudentFee?.id ? Number(this.selectedStudentFee.final_amount) : null;
  }

  protected get isPaymentOverLimit(): boolean {
    const amount = Number(this.paymentAmount);

    if (!amount) {
      return false;
    }

    const remaining = this.paymentRemainingBalance;
    const finalAmount = this.selectedStudentFeeFinalAmount;

    const overRemaining = remaining !== null && amount > remaining;
    const overFinal = finalAmount !== null && amount > finalAmount;

    return overRemaining && overFinal;
  }

  protected openRecordPayment(fee: IStudentFee['payload']): void {
    this.selectedStudentFee = fee;

    this.getStudentPayments(fee.id);

    this.paymentAmount = '';
    this.paymentDate = '';
    this.paymentMethod = '';
    this.transactionReference = '';
    this.additionalNotes = '';

    this.setRecordPayment.set(true);
  }

  protected recordPayment(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.selectedStudentFee?.id) {
      this.flashService.failure('Select a fee');
      return;
    }

    this.isSubmitting.set(true);

    const paymentForm = {
      student_id: this.studentId,
      student_fee_id: this.selectedStudentFee.id,
      amount: this.paymentAmount,
      payment_date: this.paymentDate,
      payment_method: this.paymentMethod,
      transaction_reference: this.transactionReference,
      notes: this.additionalNotes,
    };

    this.feeService.create(`${ApiEndpoints.url}/payment`, paymentForm).subscribe({
      next: () => {
        this.flashService.success('Payment recorded successfully.');
        this.isSubmitting.set(false);
        this.setRecordPayment.set(false);
        this.getStudentFees();
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.flashService.failure(err?.error?.message ?? 'Error recording payment. Try again.');
      },
    });
  }

  protected applyDiscount(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (this.adjustmentAmount && !this.adjustmentReason) {
      this.flashService.failure('Must provide a reason for adjustment');
      return;
    }

    let discount = 0;

    if (this.discountAmount) {
      discount = (this.discountAmount / 100) * Number(this.selectedStudentFee.original_amount);
    }

    const discountForm = Object.fromEntries(
      Object.entries({
        discount_amount: discount,
        adjustment_amount: this.adjustmentAmount,
        adjustment_reason: this.adjustmentReason?.trim(),
      }).filter(([, value]) => value !== undefined && value !== null && value !== ''),
    );

    if (!Object.keys(discountForm).length) {
      this.flashService.failure('No changes to apply');
      return;
    }

    this.isSubmitting.set(true);

    this.feeService
      .edit(`${ApiEndpoints.url}/student-fee/${this.selectedStudentFee.id}`, discountForm)
      .subscribe({
        next: () => {
          this.flashService.success('Fee updated successfully');
          this.getStudent(this.studentId);
          this.getStudentFees();
          this.setPaymentDetail.set(false);
          this.setDiscount.set(false);
          this.isSubmitting.set(false);
          this.adjustmentReason = '';
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error updating fee. Try again.');
        },
      });
  }

  protected waiveFee(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    const waiveForm = {
      status: 'WAIVED',
      waiver_reason: this.waiveReason,
    };

    this.isSubmitting.set(true);

    this.feeService
      .edit(`${ApiEndpoints.url}/student-fee/${this.selectedStudentFee.id}`, waiveForm)
      .subscribe({
        next: () => {
          this.flashService.success('Fee waived successfully');
          this.getStudent(this.studentId);
          this.getStudentFees();
          this.setPaymentDetail.set(false);
          this.setWaive.set(false);
          this.isSubmitting.set(false);
          this.waiveReason = '';
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error waiving fee. Try again.');
        },
      });
  }

  protected reverseWaiver(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isSubmitting.set(true);

    this.feeService
      .edit(`${ApiEndpoints.url}/student-fee/${this.selectedStudentFee.id}/reverse`, {
        reversal_reason: this.reversalReason,
      })
      .subscribe({
        next: () => {
          this.flashService.success('Fee waiver reversed successfully');
          this.getStudent(this.studentId);
          this.getStudentFees();
          this.setPaymentDetail.set(false);
          this.setReverseWaiver.set(false);
          this.isSubmitting.set(false);
          this.reversalReason = '';
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error reversing waiver. Try again.');
        },
      });
  }

  protected openAssignClass(): void {
    this.assignClassCheck.set(true);
  }

  protected getClasses(): void {
    if (!this.academicYearId) return;

    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
      academic_year_id: this.academicYearId,
    };

    this.classService.getClasses(`${ApiEndpoints.url}/class`, params).subscribe({
      next: (res) => {
        this.classes.set(res);
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load classes. Refresh the page.',
        );
      },
    });
  }

  protected selectClassById(id: string): void {
    const classItem = this.classes()?.payload.find((c) => c.id === id);

    this.classId = id;

    if (classItem) {
      this.className = classItem.level_name + classItem.name;
      this.classPublicId = classItem.public_id;
    }
  }

  protected getAcademicYear(id: string): void {
    this.academicYearService
      .getAcademicYearById(`${ApiEndpoints.url}/academic-year/${id}`)
      .subscribe({
        next: (res) => {
          this.academicYear.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Year. Refresh the page.',
          );
        },
      });
  }

  protected onAcademicYearChange(): void {
    const year = this.academicYears()?.payload.find((a) => a.id === this.academicYearId);

    if (year) {
      this.startDate = new Date(year.start_date).toISOString().split('T')[0];
      this.endDate = new Date(year.end_date).toISOString().split('T')[0];
    }

    this.classId = '';
    this.className = '';
    this.classPublicId = '';
    this.classes.set(null);

    this.getAcademicYear(this.academicYearId);
    this.getClasses();
  }

  protected assignStudentToClass(form: NgForm): void {
    if (form.invalid || !this.classId) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    this.isSubmitting.set(true);

    const enrollmentForm = {
      student_id: this.studentId,
      class_id: this.classId,
      academic_year_id: this.academicYearId,
      enrollment_date: this.enrollmentDate,
      start_date: this.startDate,
      end_date: this.endDate,
    };

    this.studentService
      .createStudent(`${ApiEndpoints.url}/student-enrollment`, enrollmentForm)
      .subscribe({
        next: () => {
          this.getStudentEnrollment(this.studentId);
          this.getStudentFees();
          this.isSubmitting.set(false);
          this.assignClassCheck.set(false);
          this.enrollmentDate = '';
          this.startDate = '';
          this.endDate = '';
          this.classId = '';
          this.className = '';
          this.classPublicId = '';
          this.academicYearId = '';
          this.academicYear.set(null);
          this.flashService.success('Student assigned to class successfully.');
          form.resetForm();
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error assigning student. Try again.');
        },
      });
  }

  protected getWaivedByUser(id: string): void {
    this.userService.getUserById(`${ApiEndpoints.url}/user/${id}`).subscribe({
      next: (res) => {
        this.waivedBy.set(res);
      },
    });
  }

  protected openAssignFee(): void {
    this.setAssignFee.set(true);
  }

  protected closeAssignFee(): void {
    this.setAssignFee.set(false);
    this.feeQuery = '';
    this.selectedFee = undefined;
    this.selectedFeeStructure.set(null);
    this.discountAmount = 0;
    this.adjustmentAmount = 0;
    this.adjustmentReason = '';
  }

  protected getFeesToAssign(value: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 20,
    };

    if (value) {
      params['search'] = value;
    }

    this.feeService.getFeeStructures(`${ApiEndpoints.url}/fee-structure`, params).subscribe({
      next: (res) => {
        this.feeStructuresToAssign.set(res);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load fees. Refresh the page');
      },
    });
  }

  protected selectFeeToAssign(fee: IFeeStructure['payload']): void {
    this.feeQuery = '';
    this.selectedFee = fee;
    this.selectedFeeStructure.set(null);
    this.discountAmount = 0;
    this.adjustmentAmount = 0;
    this.adjustmentReason = '';

    const enrollment = this.studentEnrollment()?.payload?.[0];

    if (!enrollment) {
      this.flashService.failure('Assign student to a class first');
      return;
    }

    this.findFee(enrollment.level_id, fee.fee_name, enrollment.academic_year_id);
  }

  protected findFee(levelId: string, name: string, academicYear: string): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 5,
      level_id: levelId,
      search: name,
      academic_year_id: academicYear,
    };

    this.feeService.getFeeStructures(`${ApiEndpoints.url}/fee-structure`, params).subscribe({
      next: (res) => {
        if (!res.payload.length) {
          this.flashService.failure('This fee is not assignable to this student');
          this.selectedFee = undefined;
          return;
        }

        const feeStructureId = res.payload[0].fee_structure_ids[0];

        if (!feeStructureId) {
          this.flashService.failure('This fee is not assignable to this student');
          this.selectedFee = undefined;
          return;
        }

        this.selectedFeeStructure.set(res);

        const studentFeeParams: Record<string, string | number> = {
          page: 0,
          size: 1,
          student_id: this.studentId,
          fee_structure_id: feeStructureId,
        };

        this.feeService
          .getStudentFees(`${ApiEndpoints.url}/student-fee`, studentFeeParams)
          .subscribe({
            next: (studentFeeRes) => {
              if (studentFeeRes.payload.length) {
                this.flashService.failure('This fee has already been assigned to this student');
                this.selectedFee = undefined;
                this.selectedFeeStructure.set(null);
              }
            },
            error: (err) => {
              this.flashService.failure(
                err?.error?.message ?? 'Could not verify fee assignment. Refresh the page',
              );
            },
          });
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load fees. Refresh the page');
      },
    });
  }

  protected assignFee(form: NgForm): void {
    if (form.invalid || !this.selectedFee) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    const enrollment = this.studentEnrollment()?.payload?.[0];

    if (!enrollment) {
      this.flashService.failure('Assign student to a class first');
      return;
    }

    if (this.adjustmentAmount && !this.adjustmentReason) {
      this.flashService.failure('Must provide a reason for adjustment');
      return;
    }

    this.isSubmitting.set(true);

    let discount = 0;

    if (this.discountAmount) {
      discount = (this.discountAmount / 100) * Number(this.selectedFee?.amount ?? 0);
    }

    const assignFeeForm = {
      student_id: this.studentId,
      enrollment_id: enrollment.id,
      fee_structure_id: this.selectedFeeStructure()?.payload?.[0]?.fee_structure_ids?.[0],
      discount_amount: discount,
      adjustment_amount: this.adjustmentAmount,
      adjustment_reason: this.adjustmentReason?.trim(),
    };

    this.feeService.create(`${ApiEndpoints.url}/student-fee/assign`, assignFeeForm).subscribe({
      next: () => {
        this.flashService.success('Fee assigned successfully.');
        this.isSubmitting.set(false);
        this.closeAssignFee();
        this.getStudentFees();
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.flashService.failure(err?.error?.message ?? 'Error assigning fee. Try again.');
      },
    });
  }
}
